import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useAppId } from "../context/AppIdContext";
import Header from '../component/header';
import { useLocation } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PrintVoterSlip = () => {
    const {
        appId,
        userId,
        userName,   
        vidhanSabhaName,
        vidhanSabhaNo,
        candidateName,
        candidateNo,
        partyFullName,
        savedPrinter,
        setSavedPrinter,
        clearSavedPrinter,
        partyLogoPng,
        aacharSahita // ✅ ADDED aacharSahita from context
    } = useAppId();
    const slipRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [receiptData, setReceiptData] = useState(null);
    const [status, setStatus] = useState("Ready");
    const [isPrinting, setIsPrinting] = useState(false);

    const location = useLocation();
    const voter_id = location.state?.voter_id;

    useEffect(() => {
        if (appId && voter_id) {
            fetchReceiptData();
        } else if (!voter_id) {
            setLoading(false);
            setStatus("No Voter ID provided.");
        }
    }, [appId, voter_id]);

    const fetchReceiptData = async () => {
        setLoading(true);
        try {
            const payload = {
                app_id: appId,
                voter_id: voter_id,
                user_id: userId
            };

            const response = await axios.post(`${BASE_URL}/api/print_receipt_in_printer`, payload);

            if (response.data.success && response.data.data && response.data.data.Success === "1") {
                const voterInfo = response.data.data.result[0];

                let imageUrl = "";
                if (response.data.data.result2 && response.data.data.result2.length > 0) {
                    imageUrl = response.data.data.result2[0].img;
                }

                setReceiptData({
                    ...voterInfo,
                    img: imageUrl
                });
            } else {
                setReceiptData(null);
            }
        } catch (error) {
            console.error("Error fetching receipt data:", error);
            setStatus("Failed to load voter data.");
        } finally {
            setLoading(false);
        }
    };

    const getSafeImageUrl = (url) => {
        if (!url) return "";
        let safeUrl = url.replace("http://", "https://");
        const parts = safeUrl.split("://");
        if (parts.length === 2) {
            safeUrl = parts[0] + "://" + parts[1].replace(/\/\/+/g, "/");
        }
        return safeUrl;
    };

    const convertCanvasToEscPos = (canvas) => {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const width = canvas.width;
        const height = canvas.height;
        const imgData = ctx.getImageData(0, 0, width, height).data;

        const bytesPerRow = Math.ceil(width / 8);

        const header = new Uint8Array([
            0x1B, 0x40,
            0x1B, 0x61, 0x00,
            0x1D, 0x76, 0x30, 0x00,
            bytesPerRow & 0xFF, (bytesPerRow >> 8) & 0xFF,
            height & 0xFF, (height >> 8) & 0xFF
        ]);

        const data = new Uint8Array(bytesPerRow * height);

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const idx = (y * width + x) * 4;
                const r = imgData[idx];
                const g = imgData[idx + 1];
                const b = imgData[idx + 2];
                const a = imgData[idx + 3];

                const luminance = (r * 0.299 + g * 0.587 + b * 0.114);
                const isBlack = a > 128 && luminance < 128;

                if (isBlack) {
                    const byteIdx = (y * bytesPerRow) + Math.floor(x / 8);
                    const bit = 7 - (x % 8);
                    data[byteIdx] |= (1 << bit);
                }
            }
        }

        const paperCut = new Uint8Array([0x0A, 0x0A, 0x0A, 0x0A, 0x1B, 0x61, 0x00]);
        const result = new Uint8Array(header.length + data.length + paperCut.length);
        result.set(header);
        result.set(data, header.length);
        result.set(paperCut, header.length + data.length);

        return result;
    };

    const sendDataToPrinter = async (device, printData) => {
        setStatus("Connecting to printer...");
        const server = await device.gatt.connect();
        const services = await server.getPrimaryServices();

        let printChar = null;
        for (const service of services) {
            const chars = await service.getCharacteristics();
            for (const char of chars) {
                if (char.properties.write || char.properties.writeWithoutResponse) {
                    printChar = char;
                    break;
                }
            }
            if (printChar) break;
        }

        if (!printChar) throw new Error("Writable characteristic not found.");

        setStatus("Printing fast...");

        const CHUNK_SIZE = 64;
        for (let i = 0; i < printData.length; i += CHUNK_SIZE) {
            const chunk = printData.slice(i, i + CHUNK_SIZE);
            if (printChar.properties.writeWithoutResponse) {
                await printChar.writeValueWithoutResponse(chunk);
            } else {
                await printChar.writeValue(chunk);
            }
            await new Promise(resolve => setTimeout(resolve, 10));
        }

        setStatus("Printed Successfully!");
        server.disconnect();
    };

    const handlePrint = async () => {
        if (!navigator.bluetooth) {
            alert("Web Bluetooth API is not available in your browser.");
            return;
        }

        setIsPrinting(true);
        setStatus("Capturing layout...");

        try {
            const rawCanvas = await html2canvas(slipRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#FFFFFF"
            });

            const TARGET_WIDTH = 384;
            const scaleFactor = TARGET_WIDTH / rawCanvas.width;
            const TARGET_HEIGHT = Math.floor(rawCanvas.height * scaleFactor);

            const finalCanvas = document.createElement('canvas');
            finalCanvas.width = TARGET_WIDTH;
            finalCanvas.height = TARGET_HEIGHT;
            const ctx = finalCanvas.getContext('2d');

            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT);
            ctx.drawImage(rawCanvas, 0, 0, rawCanvas.width, rawCanvas.height, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);

            setStatus("Converting to printer format...");
            const printData = convertCanvasToEscPos(finalCanvas);

            if (savedPrinter) {
                try {
                    setStatus(`Reconnecting to ${savedPrinter.name}...`);
                    const device = await navigator.bluetooth.requestDevice({
                        filters: [{ name: savedPrinter.name }],
                        optionalServices: [
                            '000018f0-0000-1000-8000-00805f9b34fb',
                            'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
                            '49535343-fe7d-4ae5-8fa9-9fafd205e455'
                        ]
                    });
                    await sendDataToPrinter(device, printData);
                    return;
                } catch (reconnectError) {
                    console.warn("Saved printer reconnect failed, opening picker:", reconnectError);
                    setStatus("Saved printer unavailable. Select printer...");
                }
            } else {
                setStatus("Select your printer...");
            }

            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: [
                    '000018f0-0000-1000-8000-00805f9b34fb',
                    'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
                    '49535343-fe7d-4ae5-8fa9-9fafd205e455'
                ]
            });

            setSavedPrinter({ id: device.id, name: device.name });

            await sendDataToPrinter(device, printData);

        } catch (error) {
            console.error(error);
            if (error.name === 'NotFoundError') {
                setStatus("Print cancelled.");
            } else {
                setStatus(`Print Failed: ${error.message}`);
            }
        } finally {
            setIsPrinting(false);
            setTimeout(() => setStatus("Ready"), 4000);
        }
    };

    return (
        <div className="dashboard-container">
            <div className="dashboard-header-wrapper">
                <Header title="Voter Slip Printing" />
            </div>

            <main className="dashboard-main">

                <div className="modern-card controls-card mb-4 slide-up-element">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h3 className="m-0 fw-bold text-slate">Print Queue</h3>
                            <p className="m-0 text-muted mt-1">
                                Status: <span className="fw-bold" style={{ color: '#8B5CF6' }}>{status}</span>
                            </p>
                            {savedPrinter && (
                                <p className="m-0 mt-1" style={{ fontSize: '0.82rem', color: '#64748B' }}>
                                    🖨️ <span className="fw-bold">{savedPrinter.name || "Saved Printer"}</span>
                                    <button
                                        onClick={clearSavedPrinter}
                                        style={{
                                            marginLeft: '10px',
                                            background: 'none',
                                            border: 'none',
                                            color: '#EF4444',
                                            fontSize: '0.78rem',
                                            cursor: 'pointer',
                                            fontWeight: '700',
                                            padding: '0'
                                        }}
                                    >
                                        Change
                                    </button>
                                </p>
                            )}
                        </div>

                        <button
                            className="btn-print-main"
                            onClick={handlePrint}
                            disabled={isPrinting || loading || !receiptData}
                        >
                            {isPrinting ? (
                                <><div className="spinner-small me-2"></div> Processing...</>
                            ) : (
                                <><svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="me-2"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg> PRINT SLIP</>
                            )}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="loader-container mt-5">
                        <div className="spinner"></div>
                        <p className="mt-3 text-muted fw-bold">Fetching Voter Data...</p>
                    </div>
                ) : !receiptData ? (
                    <div className="modern-card text-center p-5 mt-4 text-muted">
                        No voter data found for printing.
                    </div>
                ) : (
                    <div className="preview-container slide-up-element" style={{ animationDelay: '0.1s' }}>
                        <div className="preview-label">Live Thermal Printer Preview (58mm)</div>

                        <div className="slip-preview-wrapper">
                            <div className="slip-layout" ref={slipRef}>
                                {String(aacharSahita) !== "1" && (
                                    <div className="slip-header">
                                        <h2 className="party-name">{partyFullName || "Bhartiya Janta Party"}</h2>
                                    </div>
                                )}
                                {receiptData.img && String(aacharSahita) !== "1" && (
                                    <div className="photo-section">
                                        <img
                                            src={getSafeImageUrl(receiptData.img)}
                                            alt="Candidate"
                                            className="candidate-photo"
                                            crossOrigin="anonymous"
                                        />
                                    </div>
                                )}

                                <div className="constituency-box">
                                    <h3>{vidhanSabhaNo || "Constituency No."}-{vidhanSabhaName || "Constituency Name"}</h3>
                                </div>

                                <div className="voter-details">
                                    <div className="detail-row mt-2">
                                        <span className="fw-bold">{receiptData.first_detail || ""}</span>
                                    </div>
                                    <div className="detail-row mt-3">
                                        <span className="fw-bold">{receiptData.full_name || ""}</span>
                                    </div>
                                    {receiptData.middle_name && (
                                        <div className="detail-row mt-2 fw-bold">
                                            {receiptData.middle_name}
                                        </div>
                                    )}
                                    <div className="detail-row mt-3">
                                        <span className="fw-bold">{receiptData.sex_age || ""}</span>
                                    </div>
                                    <div className="detail-row mt-3">
                                        <span className="fw-bold">{receiptData.idcard_no || ""}</span>
                                    </div>
                                    <div className="detail-row mt-3">
                                        <span className="fw-bold">{receiptData.polling_location || ""}</span>
                                    </div>
                                </div>

                                {/* --- MULTIPLE CANDIDATE TABLE IMPLEMENTATION --- */}
                                {/* ✅ CONDITIONAL RENDERING FOR AACHAR SAHITA */}
                                {String(aacharSahita) !== "1" && (
                                    <div className="candidate-box" style={{ border: '1px solid #000', overflow: 'hidden', borderRadius: '4px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <tbody>
                                                {(candidateName ? candidateName.split(',') : [userName || "Candidate Name"]).map((name, index, arr) => {
                                                    // Ignore trailing empty strings if the string ended with a comma
                                                    if (!name.trim()) return null;

                                                    const noArray = candidateNo ? candidateNo.toString().split(',') : ["-"];
                                                    const no = noArray[index] || "-";

                                                    // Determine if this is the last row for border styling
                                                    const isLastRow = index >= arr.length - 1 || (index === arr.length - 2 && arr[arr.length - 1].trim() === "");
                                                    const rowBorderBottom = isLastRow ? 'none' : '1.5px dashed #000';

                                                    return (
                                                        <tr key={index}>
                                                            {/* Candidate Name Column */}
                                                            <td style={{
                                                                padding: '8px 15px',
                                                                borderBottom: rowBorderBottom,
                                                                textAlign: 'left',
                                                                fontWeight: 'bold',
                                                                verticalAlign: 'middle'
                                                            }}>
                                                                {no.trim()} : {name.trim()}

                                                            </td>

                                                            {/* Party Logo Column (Now in each row) */}
                                                            <td style={{
                                                                width: '70px',
                                                                padding: '4px 8px',
                                                                borderBottom: rowBorderBottom,
                                                                borderLeft: '1.5px solid #000',
                                                                textAlign: 'center',
                                                                verticalAlign: 'middle'
                                                            }}>
                                                                <img
                                                                    src={partyLogoPng}
                                                                    alt="Party Logo"
                                                                    crossOrigin="anonymous"
                                                                    style={{
                                                                        width: '35px',
                                                                        height: '35px',
                                                                        objectFit: 'contain',
                                                                        display: 'block',
                                                                        margin: '0 auto'
                                                                    }}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div className="slip-footer">
                                    <p className="appeal-text">कृपया अपना कीमती वोट देकर विजय बनायें</p>
                                    <h2 className="thank-you">धन्यवाद</h2>
                                </div>

                            </div>
                        </div>

                    </div>
                )}
            </main >

            <style jsx>{`
                .dashboard-container { background: #F4F5F8; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; width: 100%; box-sizing: border-box; padding-bottom: 40px; }
                @keyframes slideInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
                .slide-up-element { opacity: 0; animation: slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .dashboard-header-wrapper { padding: 16px 20px; }
                @media (min-width: 768px) { .dashboard-header-wrapper { padding: 25px 25px 0 25px; } }
                .dashboard-main { padding: 12px 16px; max-width: 1400px; margin: 0 auto; }
                .modern-card { background: #FFFFFF; border-radius: 20px; padding: 20px 24px; box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.05); border: 1px solid #F1F5F9; }
                .text-slate { color: #1E293B; }
                .btn-print-main { display: inline-flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%); color: white; border: none; padding: 12px 32px; border-radius: 12px; font-size: 1.05rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25); }
                .btn-print-main:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(139, 92, 246, 0.35); }
                .btn-print-main:disabled { background: #CBD5E1; box-shadow: none; cursor: not-allowed; }
                .spinner-small { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top: 2px solid #FFFFFF; border-radius: 50%; animation: spin 1s linear infinite; }
                .loader-container { display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 60px 0; }
                .spinner { width: 40px; height: 40px; border: 4px solid #F1F5F9; border-top: 4px solid #8B5CF6; border-radius: 50%; animation: spin 1s linear infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                
                .preview-container { display: flex; flex-direction: column; align-items: center; margin-top: 20px; }
                .preview-label { font-size: 0.85rem; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; background: #E2E8F0; padding: 6px 16px; border-radius: 20px; }
                .slip-preview-wrapper { background: white; padding: 20px; border-radius: 12px; box-shadow: 0 15px 35px rgba(0,0,0,0.1); border: 1px solid #E2E8F0; position: relative; }
                .slip-preview-wrapper::before { content: ""; position: absolute; top: -6px; left: 0; right: 0; height: 6px; background-size: 12px 6px; background-image: radial-gradient(circle at 6px 0, transparent 6px, white 7px); }
                
                .slip-layout { width: 384px; margin: 0; background: white; color: black; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 10px 15px; box-sizing: border-box; }
                
                .slip-header { display: flex; justify-content: center; align-items: center; gap: 10px; margin-bottom: 15px; }
                .photo-section { display: flex; justify-content: center; margin-bottom: 20px; }
                .candidate-photo { width: 260px; height: auto; border-radius: 8px; border: 2px solid #000; }
                .constituency-box { border: 2.5px dashed #000; padding: 12px; text-align: center; border-radius: 6px; margin-bottom: 20px; }
                .candidate-box { display: flex; align-items: center; border: 2.5px dashed #000; border-radius: 6px; padding: 12px;}
                .candidate-logo img { width: 36px; height: 36px; }
                .slip-footer { text-align: center; margin-top: 30px; padding-bottom: 10px; color: #000; }
                
                .mt-1 { margin-top: 4px; }
                .mt-2 { margin-top: 8px; }
                .mt-3 { margin-top: 14px; }

                .party-name { font-size: 1.5rem; font-weight: 900; margin: 0; color: #000; text-align: center; }
                .constituency-box h3 { margin: 0; font-size: 1.4rem; font-weight: 900; color: #000; }
                .voter-details { font-size: 1.25rem; line-height: 1.6; margin-bottom: 24px; color: #000; }
                .fw-bold { font-weight: 900; }
                .candidate-number { font-size: 1.4rem; font-weight: 900; padding-right: 15px; border-right: 2.5px dashed #000; color: #000; }
                .candidate-name { flex: 1; padding-left: 15px; font-size: 1.4rem; font-weight: 900; color: #000; }
                .appeal-text { font-size: 1.15rem; font-weight: 800; margin-bottom: 8px; }
                .thank-you { font-size: 2.0rem; font-weight: 900; margin: 0; }
            `}</style>
        </div >
    );
};

export default PrintVoterSlip;